import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput
} from '@mantine/core';
import { format } from 'date-fns';
import { Context as EcommerceContext } from '../../../providers/EcommerceProvider';
import DeleteItemView from '../../common/DeleteItemView';
import ResponsiveModal from '../../common/ResponsiveModal';
import { CURRENCY_TYPE_ENUM } from '../../../config/constants';
import { stripUtcDateFormatting } from '../../../helpers/formatHelper';
import ConfirmCheckbox from '../../common/ConfirmCheckbox.';

const EcomVendorSettingsView = ({ ecomVendor }) => {
  const history = useHistory();
  const { updateEcomVendor, deleteEcomVendor } = useContext(EcommerceContext);
  const [modalState, setModalState] = useState({
    isOpen: false,
    action: '',
    item: null
  });
  const [formState, setFormState] = useState({
    name: '',
    domain: '',
    supportEmail: '',
    sandboxEnabled: false,
    isDemoVendor: false,
    fkCurrencyType: CURRENCY_TYPE_ENUM.CAD,
    reminderDelay: 0,
    hasUnsavedChanges: false,
    error: null,
    loading: false
  });

  useEffect(() => {
    if (ecomVendor) {
      setFormState({
        name: ecomVendor.name,
        domain: ecomVendor.domain ?? '',
        supportEmail: ecomVendor.supportEmail ?? '',
        sandboxEnabled: ecomVendor.sandboxEnabled,
        isDemoVendor: ecomVendor.isDemoVendor,
        fkCurrencyType: ecomVendor.fkCurrencyType,
        reminderDelay: ecomVendor.reminderDelay ?? 0,
        hasUnsavedChanges: false,
        error: null,
        loading: false
      });
    }
  }, [ecomVendor]);

  return (
    <Stack sx={{ gap: 20 }}>
      <Stack
        component="form"
        onSubmit={(e) => {
          e.preventDefault();

          setFormState({
            ...formState,
            error: null,
            loading: true
          });
          updateEcomVendor(
            ecomVendor.pkEcomVendor,
            formState,
            () => {
              setFormState({
                ...formState,
                loading: false,
                error: null,
                hasUnsavedChanges: false
              });
            },
            (error) => {
              setFormState({
                ...formState,
                loading: false,
                error
              });
            }
          );
        }}
        sx={{ gap: 10 }}
      >
        <TextInput
          disabled={formState.loading}
          label="Vendor Name"
          onChange={(e) =>
            setFormState({
              ...formState,
              name: e.currentTarget.value,
              hasUnsavedChanges: true
            })
          }
          required
          value={formState.name}
        />

        <TextInput
          disabled={formState.loading}
          label="Domain"
          onChange={(e) =>
            setFormState({
              ...formState,
              domain: e.currentTarget.value,
              hasUnsavedChanges: true
            })
          }
          value={formState.domain}
        />

        <TextInput
          disabled={formState.loading}
          label="Support Email"
          onChange={(e) =>
            setFormState({
              ...formState,
              supportEmail: e.currentTarget.value,
              hasUnsavedChanges: true
            })
          }
          type="email"
          value={formState.supportEmail}
        />

        <Select
          data={[
            {
              label: 'CAD',
              value: CURRENCY_TYPE_ENUM.CAD
            },
            {
              label: 'USD',
              value: CURRENCY_TYPE_ENUM.USD
            }
          ]}
          disabled={formState.loading}
          label="Currency"
          onChange={(v) =>
            setFormState({
              ...formState,
              fkCurrencyType: v
            })
          }
          value={formState.fkCurrencyType}
        />

        <Group style={{ gap: 5, flex: 1 }}>
          <TextInput
            disabled
            label="Last Reminded"
            onChange={() => {}}
            style={{ flex: 1 }}
            value={
              ecomVendor.lastReminded
                ? format(
                    new Date(
                      `${stripUtcDateFormatting(ecomVendor.lastReminded)}Z`
                    ),
                    'PPp'
                  )
                : 'Never'
            }
          />
          <NumberInput
            disabled={formState.loading}
            label="Reminder Delay"
            min={0}
            onChange={(v) =>
              setFormState({
                ...formState,
                reminderDelay: v,
                hasUnsavedChanges: true
              })
            }
            style={{ flex: 1 }}
            value={formState.reminderDelay}
          />
        </Group>

        <Switch
          checked={formState.sandboxEnabled}
          disabled={formState.loading}
          label="Sandbox Mode"
          onChange={() =>
            setFormState({
              ...formState,
              sandboxEnabled: !formState.sandboxEnabled,
              hasUnsavedChanges: true
            })
          }
        />

        {!ecomVendor.isDemoVendor && (
          <ConfirmCheckbox
            checked={formState.isDemoVendor}
            confirmDisabled={ecomVendor.hasStoreProductHistory}
            confirmMessage={(
              <Stack style={{ gap: 16, marginTop: 20 }}>
                {ecomVendor.hasStoreProductHistory ? (
                  <>
                    <Text style={{ fontSize: 14 }}>
                      This vendor cannot be switched to demo mode due to its
                      store product history.
                    </Text>
                  </>
                ) : (
                  <>
                    <Text style={{ fontSize: 14 }}>
                      Are you sure this is a demo vendor?
                    </Text>

                    <Text style={{ fontSize: 14 }}>
                      Once a vendor is switched to demo mode, it cannot be
                      reverted. Please confirm that this vendor is intended for
                      demo purposes only.
                    </Text>

                    <Text style={{ fontSize: 14, fontWeight: 500 }}>
                      This action cannot be undone.
                    </Text>
                  </>
                )}
              </Stack>
            )}
            confirmTitle={
              ecomVendor.hasStoreProductHistory
                ? 'Demo mode unavailable'
                : 'Demo warning'
            }
            description="Restrict vendor visibility to SportsHeadz staff only; the demo vendor is intended for demonstration purposes and should not be used for real transactions."
            disabled={formState.loading}
            label="Make this a demo vendor"
            onChange={() =>
              setFormState({
                ...formState,
                isDemoVendor: !formState.isDemoVendor,
                hasUnsavedChanges: true
              })
            }
            showConfirmModal={!formState.isDemoVendor}
            variant="switch"
          />
        )}

        <Group style={{ justifyContent: 'space-between', marginTop: 10 }}>
          <Group>
            <Button
              color={ecomVendor.deleted ? 'Reinstate' : 'red'}
              compact
              disabled={formState.loading}
              onClick={() =>
                setModalState({
                  ...modalState,
                  isOpen: true,
                  action: ecomVendor.deleted ? 'reinstate' : 'delete',
                  item: ecomVendor
                })
              }
              variant={ecomVendor.deleted ? 'outline' : 'filled'}
            >
              {ecomVendor.deleted ? 'Reinstate Vendor' : 'Delete Vendor'}
            </Button>
          </Group>
          {formState.hasUnsavedChanges && (
            <Group sx={{ justifyContent: 'end' }}>
              <Button
                color="dark"
                compact
                disabled={formState.loading}
                onClick={() =>
                  setFormState({
                    name: ecomVendor.name,
                    domain: ecomVendor.domain ?? '',
                    supportEmail: ecomVendor.supportEmail ?? '',
                    sandboxEnabled: ecomVendor.sandboxEnabled,
                    fkCurrencyType: ecomVendor.fkCurrencyType,
                    hasUnsavedChanges: false,
                    loading: false
                  })
                }
                type="button"
              >
                Cancel
              </Button>
              <Button compact loading={formState.loading} type="submit">
                Save Changes
              </Button>
            </Group>
          )}
        </Group>

        {formState.error && (
          <Alert color="red" variant="outline">
            <Text weight={500}>{formState.error}</Text>
          </Alert>
        )}
      </Stack>

      <ResponsiveModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={
          modalState.action === 'reinstate'
            ? 'Reinstate Vendor'
            : 'Delete Vendor'
        }
      >
        <DeleteItemView
          isLoading={formState.loading}
          itemLabel="Vendor"
          onCancel={() => setModalState({ ...modalState, isOpen: false })}
          onDelete={() => {
            setFormState({
              ...formState,
              loading: true
            });

            if (modalState.action === 'reinstate') {
              updateEcomVendor(
                ecomVendor.pkEcomVendor,
                { restoreVendor: true },
                () => {
                  setModalState({ ...modalState, isOpen: false });
                },
                (error) => {
                  setFormState({
                    ...formState,
                    loading: false,
                    error
                  });
                  setModalState({ ...modalState, isOpen: false });
                }
              );
            }
            else {
              deleteEcomVendor(
                ecomVendor.pkEcomVendor,
                null,
                () => {
                  setModalState({ ...modalState, isOpen: false });
                  history.push(`/ecommerce/vendors`);
                },
                (error) => {
                  setFormState({
                    ...formState,
                    isLoading: false,
                    error
                  });
                  setModalState({ ...modalState, isOpen: false });
                }
              );
            }
          }}
          reinstate={modalState.action === 'reinstate'}
          warnings={[
            'The ability to publicly view the vendor on the ecommerce site.'
          ]}
        />
      </ResponsiveModal>
    </Stack>
  );
};

EcomVendorSettingsView.propTypes = { ecomVendor: PropTypes.object };

export default EcomVendorSettingsView;
